import { useEffect, useState } from "react";

/**
 * Tiny refresh button pinned to the bottom-right corner.
 *
 * Design goals:
 *   1. Completely invisible on an OBS Browser Source (no hover, no keyboard)
 *      so the public stream stays 100% clean.
 *   2. Appear ONLY when the operator hovers a small hot-zone in the corner
 *      (the user explicitly asked for pure-hover, not mousemove-anywhere).
 *   3. Still clickable while visible.
 *
 * Implementation: a 60×60 transparent hot-zone wraps the button.
 *   - Hot-zone pointer-events: auto (so it can receive hover).
 *   - Button opacity 0 by default, 1 on hover — no JS state, no timers.
 *   - We keep a small useState only to bump opacity via React's :hover fallback
 *     because inline styles do not support pseudo-classes.
 */
export const ReloadButton = () => {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: "fixed",
        bottom: 0,
        right: 0,
        width: 60,
        height: 60,
        zIndex: 9999,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "flex-end",
        padding: 12,
        // The hot-zone itself is transparent but must receive pointer
        // events — otherwise :hover cannot fire on OBS Interact mode either.
        pointerEvents: "auto",
        background: "transparent",
      }}
    >
      <button
        type="button"
        onClick={() => window.location.reload()}
        aria-label="Refresh"
        data-testid="pwa-refresh-btn"
        style={{
          width: 36,
          height: 36,
          borderRadius: 999,
          border: "1px solid rgba(255,255,255,0.15)",
          background: "rgba(0,0,0,0.55)",
          backdropFilter: "blur(6px)",
          color: "rgba(255,255,255,0.9)",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          opacity: hovered ? 1 : 0,
          transition: "opacity 180ms ease-out",
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="1 4 1 10 7 10" />
          <polyline points="23 20 23 14 17 14" />
          <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" />
        </svg>
      </button>
    </div>
  );
};

export default ReloadButton;
